import { SearchQConfigsDocument, useDuplicateQConfigMutation } from '@exam-management/graphql';
import { useNavigate, useParams } from '@exam-management/router';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  FormTextField,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';

import {
  QConfigDuplicationFormProvider,
  useQConfigDuplicationFormContext,
  type QConfigDuplicationFormInput,
} from '../../providers';

interface QConfigDuplicationModalProps {
  onClose: () => void;
  open: boolean;
  qConfigId: string;
  name: string;
}

interface DuplicateFormProps {
  onClose: () => void;
  open: boolean;
  qConfigId: string;
}

function QConfigDuplicationForm(props: Readonly<DuplicateFormProps>) {
  const { onClose, open: isOpen, qConfigId } = props;
  const formContext = useQConfigDuplicationFormContext();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { courseId } = useParams('/exam-courses/:courseId/qconfigs');

  const [duplicateQConfig, { loading: isDuplicateQConfigLoading }] = useDuplicateQConfigMutation({
    onCompleted: (data) => {
      showSnackbar({ message: 'QConfig Duplicated successfully', variant: 'success' });
      navigate('/exam-courses/:courseId/qconfigs/:qconfigId', {
        params: {
          courseId: courseId,
          qconfigId: data.excDuplicateQConfig.qConfig.id,
        },
      });
    },
    onError: () => {
      showSnackbar({ message: 'Unable to Duplicated QConfig', variant: 'error' });
    },
    awaitRefetchQueries: true,
    refetchQueries: [SearchQConfigsDocument],
  });

  const onSubmit = async (data: QConfigDuplicationFormInput) => {
    await duplicateQConfig({
      variables: {
        duplicateQConfigInput: {
          qConfigId,
          name: data.qConfigName,
        },
      },
    });
    onClose();
  };

  return (
    <Dialog open={isOpen}>
      <DialogTitle onClose={onClose}>
        <Text variant='h2'>Duplicate QConfig</Text>
      </DialogTitle>
      <Divider />
      <Form>
        <DialogContent>
          <FormTextField name='qConfigName' label='ชื่อ QConfig' placeholder='กรอกชื่อ QConfig' />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={onClose} color='secondary' variant='outlined' size='large'>
            Cancel
          </Button>
          <Button
            onClick={formContext.handleSubmit(onSubmit)}
            size='large'
            loading={isDuplicateQConfigLoading}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
}

export default function QConfigDuplicationModal(props: Readonly<QConfigDuplicationModalProps>) {
  const { onClose, open: isOpen, qConfigId, name } = props;

  return (
    <QConfigDuplicationFormProvider oldQConfigName={name}>
      <QConfigDuplicationForm onClose={onClose} open={isOpen} qConfigId={qConfigId} />
    </QConfigDuplicationFormProvider>
  );
}
